// src/pages/BillDetail.tsx — one contractor (or agent) bill, as its own page (Finance → Bills → a row, or New bill).
//
// One component for /app/bills/new, a draft (edited in place) and an approved bill (read-only), laid out like the invoice
// page (pages/InvoiceDetail.tsx): header with status and a ⋮ menu, then a tab strip: Summary (the bill itself, edited
// in place while a draft), Details (its fields), Payments (pay it, with the funding split, and reverse a payment),
// Documents (the supplier's invoice, quotes, receipts) and Notes. ?tab= picks the tab and ?void=1 opens Void, so the
// list's row menu can link straight to them.
//
// Prices are VAT-INCLUSIVE: the unit price typed (or taken from an item) is the gross, and the server backs the net out
// of it per line (bills.service.ts computeLines → pricing.ts). The Net / VAT / Total come from POST /bills/preview, the
// same computation saving uses; while that is in flight a local estimate is shown. Paying draws on the property's own
// balance first, then (optionally) tenant deposits, then the group company advances the shortfall — the split is asked
// of POST /bills/:id/pay-preview and /pay runs the same computation under a row lock.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, CheckCircle2, Loader2, Paperclip, Plus, ShieldAlert, ShieldCheck, Trash2, X } from 'lucide-react';
import { api, billsApi } from '../lib/api';
import { useCan } from '../hooks/usePermission';
import { documentsApi, toUpload, fileSize, BILL_DOCUMENT_CATEGORIES, MAX_UPLOAD_BYTES, type DocumentCategory } from '../lib/resident';
import { Card, Badge, Money, DateText, PageHeader, Button, Input, Select, Textarea, SearchSelect, ActionMenu, Tabs } from '../components/ui';
import { Confirm, FormError } from '../components/forms/common';
import { Can } from '../components/Can';
import { DocumentsPanel } from '../components/resident/DocumentsPanel';
import { formatDate, formatMoneyExact } from '../lib/viz';
import { AgentChip } from './Bills';
import type { Bill, BillLine, BillLocations, BillPayment, BillPreview, CreateBillBody, Item, TaxDefaults, TaxRate } from '../types/api';
import { PageLoader } from '../components/BrandLoader';

type Property = { id: string; name: string; region: 'UK' | 'AE'; entityId: string | null };
type Entity = { id: string; name: string };
type Vendor = { id: string; name: string; trade?: string | null };
type UnitOption = { id: string; reference: string; name?: string | null; buildingId: string | null; building?: { name: string } | null };
type BankAccount = { id: string; entityId: string; name: string | null; bankName: string | null; glAccountId: string | null; isActive: boolean };

/** The API's PAYMENT_METHODS (src/modules/finance/schemas.ts). */
const METHODS = [
  { value: 'BANK_TRANSFER', label: 'Bank transfer' }, { value: 'CARD', label: 'Card' }, { value: 'DIRECT_DEBIT', label: 'Direct debit' },
  { value: 'CHEQUE', label: 'Cheque' }, { value: 'CASH', label: 'Cash' }, { value: 'STRIPE', label: 'Stripe' },
] as const;
const methodLabel = (m: string | null | undefined) => (m ? METHODS.find((x) => x.value === m)?.label ?? pretty(m) : '—');

const FEE: Record<string, string> = { LETTING: 'Letting fee', RENEWAL: 'Renewal fee', WORKS: 'Works arranged' };
const today = () => new Date().toISOString().slice(0, 10);
const pct = (n: number) => `${String(Number(n.toFixed(2)))}%`;
const pretty = (s: string | null | undefined) => (s ? s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ') : '—');
const TH = 'px-3 py-1.5 text-[11.5px] font-medium uppercase tracking-wide text-ink-500';
const fmt = (minor: number, currency: string) => formatMoneyExact(minor, currency);

/** One row per VAT rate in use, as on the invoice page. */
function vatSummary(lines: Array<Pick<BillLine, 'taxCode' | 'taxRate' | 'netMinor' | 'taxMinor'>>) {
  const byRate = new Map<string, { code: string; rate: number; netMinor: number; taxMinor: number }>();
  for (const l of lines) {
    const code = l.taxCode ?? '—';
    const k = `${code}|${l.taxRate}`;
    const row = byRate.get(k) ?? { code, rate: l.taxRate, netMinor: 0, taxMinor: 0 };
    row.netMinor += l.netMinor; row.taxMinor += l.taxMinor;
    byRate.set(k, row);
  }
  return [...byRate.values()].sort((a, b) => b.rate - a.rate || a.code.localeCompare(b.code));
}

/** Major-unit text → minor units, or null when it is not a number. */
const toMinor = (s: string): number | null => {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};

const TABS = [['summary', 'Summary'], ['details', 'Details'], ['payments', 'Payments'], ['documents', 'Documents'], ['notes', 'Notes']] as const;
type Tab = (typeof TABS)[number][0];

/** Over the sign-off limit and not yet signed off by a director. */
const needsSignoff = (b: Bill) => b.requiresSignoff && !b.signedOffBy && b.status !== 'VOID' && b.status !== 'PAID';

/**
 * /app/bills/new and /app/bills/:id. A draft (with bills.update) opens in edit mode; anything else is read-only.
 * ?tab=payments|details|documents|notes opens that tab, ?void=1 opens Void (the list's row menu links here with them).
 */
export default function BillDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const canJournals = useCan('journals.read');
  const canCreate = useCan('bills.create');
  const canUpdate = useCan('bills.update');
  const canPay = useCan('bills.pay');
  const canApprove = useCan('bills.approve');
  const canDocsCreate = useCan('documents.create');
  const canDocsDelete = useCan('documents.delete');
  // The tab lives in the URL (?tab=), so it survives a reload and can be linked to.
  const tabParam = params.get('tab');
  const tab: Tab = !isNew && TABS.some(([k]) => k === tabParam) ? (tabParam as Tab) : 'summary';
  const setTab = (k: Tab) => {
    const next = new URLSearchParams(params);
    if (k === 'summary') next.delete('tab'); else next.set('tab', k);
    setParams(next, { replace: true });
  };
  // Bumped by every "Pay": the Payments tab focuses its amount field.
  const [payFocus, setPayFocus] = useState(0);
  const goPay = () => { setTab('payments'); setPayFocus((n) => n + 1); };
  const [dialogState, setDialog] = useState<'approve' | 'void' | 'signOff' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => { if (!notice) return; const t = setTimeout(() => setNotice(null), 8000); return () => clearTimeout(t); }, [notice]);

  const { data: bill, isLoading, error } = useQuery({ queryKey: ['bill', id], queryFn: () => billsApi.get(id!), enabled: !isNew });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['bill', id] });
    void qc.invalidateQueries({ queryKey: ['/bills'] });
    void qc.invalidateQueries({ queryKey: ['/property-funds'] });
    void qc.invalidateQueries({ queryKey: ['/property-funds/advances'] });
    if (bill) void qc.invalidateQueries({ queryKey: ['property-balance', bill.propertyId] });
  };

  // ?void=1 from the list's row menu opens Void; closing it drops the flag.
  const flag = params.get('void') === '1' ? 'void' : null;
  const closeDialog = () => {
    setDialog(null);
    if (flag) { const next = new URLSearchParams(params); next.delete('void'); setParams(next, { replace: true }); }
  };

  const approve = useMutation({ mutationFn: () => billsApi.approve(id!), onSuccess: () => { closeDialog(); setNotice('Bill approved — the liability is posted to the ledger.'); refresh(); } });
  const voidBill = useMutation({ mutationFn: (reason: string) => billsApi.void(id!, reason), onSuccess: () => { closeDialog(); setNotice('Bill voided.'); refresh(); } });
  const signOff = useMutation({ mutationFn: () => billsApi.signOff(id!), onSuccess: () => { closeDialog(); setNotice('Signed off — the bill can be paid.'); refresh(); } });

  if (isNew && !canCreate) return <div className="py-12 text-center text-[12.5px] text-ink-400">You cannot create bills.</div>;
  if (!isNew && error) return <div className="py-12 text-center text-[12.5px] text-red-600">{(error as Error).message}</div>;
  if (!isNew && (isLoading || !bill)) return <PageLoader />;

  const editing = isNew || (bill!.status === 'DRAFT' && canUpdate);
  const payable = !!bill && (bill.status === 'APPROVED' || bill.status === 'PART_PAID') && bill.outstandingMinor > 0;
  const voidable = !!bill && (bill.status === 'DRAFT' || bill.status === 'APPROVED') && bill.paidMinor === 0;
  const awaiting = !!bill && needsSignoff(bill);
  const dialog = dialogState ?? (flag === 'void' && voidable ? 'void' : null);
  // A bill's documents: added while it is not void; removed only until it is approved (they are the evidence for it).
  const approvedOnce = !!bill && (['APPROVED', 'PART_PAID', 'PAID'].includes(bill.status) || (bill.status === 'VOID' && !!bill.journalId));
  const journalLink = (jid: string | null, label: string) => jid && (canJournals
    ? <Link to={`/app/journals?open=${jid}`} className="text-bronze-600 hover:underline">{label}</Link>
    : <span>{label}</span>);

  return (
    <div className="mx-auto max-w-6xl space-y-2.5">
      <PageHeader back="/app/bills"
        title={isNew ? 'New bill' : bill!.kind === 'AGENT' ? <>{bill!.agentName ?? 'Agent'} <AgentChip /></> : bill!.vendorName ?? 'No vendor'}
        eyebrow={isNew ? 'Draft' : [bill!.number, bill!.propertyName, bill!.buildingName, bill!.unitReference, bill!.kind === 'AGENT' ? FEE[bill!.agentFeeType ?? 'LETTING'] : null, bill!.supplierReference ? `their ref ${bill!.supplierReference}` : null].filter(Boolean).join(' · ')}
        actions={<>{awaiting && <Badge value="Needs sign-off" tone="warning" />}{bill?.isOverdue && <Badge value="OVERDUE" />}<Badge value={bill?.status ?? 'DRAFT'} /></>}
        menu={bill ? [
          bill.status === 'DRAFT' && { label: 'Approve', icon: 'Check', can: 'bills.approve', onClick: () => setDialog('approve'), title: 'Posts the liability to the ledger' },
          payable && { label: 'Pay', icon: 'Banknote', can: 'bills.pay', onClick: goPay },
          awaiting && { label: 'Sign off', icon: 'ShieldCheck', can: 'bills.approve', onClick: () => setDialog('signOff') },
          voidable && { label: 'Void', icon: 'Ban', can: 'bills.void', danger: true, onClick: () => setDialog('void') },
          { label: 'Open the property', icon: 'Building2', to: `/app/properties/${bill.propertyId}`, separator: true },
          !!bill.tenancyId && { label: `Open ${bill.tenancyReference ?? 'the tenancy'}`, icon: 'FileSignature', to: `/app/tenancies/${bill.tenancyId}` },
          !!bill.workOrderId && { label: `Open ${bill.workOrderReference ?? 'the work order'}`, icon: 'Wrench', to: `/app/work-orders/${bill.workOrderId}` },
          !!bill.journalId && canJournals && { label: 'View journal', icon: 'BookOpen', to: `/app/journals?open=${bill.journalId}` },
        ] : []} />

      {notice && (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-[12.5px] text-emerald-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /><span className="flex-1">{notice}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss" className="rounded-md p-0.5 hover:bg-emerald-100"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {awaiting && bill && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-900">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span className="flex-1">Above the director sign-off limit. {canApprove ? 'Sign it off here, or paying it records your sign-off.' : 'A director must sign it off before it can be paid.'}</span>
          {canApprove && <Button size="sm" variant="secondary" icon="ShieldCheck" onClick={() => setDialog('signOff')}>Sign off</Button>}
        </div>
      )}

      <Tabs items={TABS.map(([k, l]) => ({ key: k, label: isNew && k !== 'summary' ? <span className="cursor-not-allowed text-ink-300" title="Save the bill first">{l}</span> : l }))}
        value={tab} onChange={(k) => { if (!(isNew && k !== 'summary')) setTab(k); }} />

      {tab === 'payments' && bill ? (
        <PaymentsTab bill={bill} payable={payable} canPay={canPay} focusToken={payFocus} journalLink={journalLink} onChanged={(msg) => { setNotice(msg); refresh(); }} />
      ) : tab === 'details' && bill ? (
        <DetailsTab key={bill.updatedAt ?? bill.id} bill={bill} canEdit={canUpdate} journalLink={journalLink} onSaved={(msg) => { setNotice(msg); refresh(); }} />
      ) : tab === 'documents' && bill ? (
        <DocumentsPanel resource="bill" resourceId={bill.id} title="Documents"
          canAdd={(canUpdate || canDocsCreate) && bill.status !== 'VOID'}
          canRemove={(canUpdate || canDocsDelete) && !approvedOnce}
          lockedNote={approvedOnce ? `This bill has been approved: its documents are the evidence for it and are kept.${bill.status === 'VOID' ? '' : ' You can still add more.'}` : null}
          onChanged={() => void qc.invalidateQueries({ queryKey: ['/bills'] })} />
      ) : tab === 'notes' && bill ? (
        <NotesTab key={bill.updatedAt ?? bill.id} bill={bill} canEdit={canUpdate} onSaved={(msg) => { setNotice(msg); refresh(); }} />
      ) : editing ? (
        <BillEditor key={bill?.updatedAt ?? 'new'} bill={bill ?? null} workOrderId={isNew ? params.get('workOrderId') : null}
          onCreated={(created, msg) => { refresh(); navigate(`/app/bills/${created.id}`, { replace: true }); if (msg) setNotice(msg); }}
          onSaved={(msg) => { setNotice(msg); refresh(); }} />
      ) : (
        <BillView bill={bill!} journalLink={journalLink} onPay={payable && canPay ? goPay : undefined} />
      )}

      {bill && (
        <>
          <Confirm open={dialog === 'approve'} onClose={closeDialog} danger={false} title={`Approve ${bill.number}?`}
            body={<>Posts Dr Repairs &amp; maintenance / Cr Accounts payable for <Money minor={bill.totalMinor} currency={bill.currency} className="font-medium text-ink-900" /> in {bill.entityName}. The bill can no longer be edited, only voided.</>}
            confirmLabel="Approve bill" pending={approve.isPending} error={approve.error} onConfirm={() => approve.mutate()} />
          <Confirm open={dialog === 'signOff'} onClose={closeDialog} danger={false} title={`Sign off ${bill.number}?`}
            body={<>Releases <Money minor={bill.totalMinor} currency={bill.currency} className="font-medium text-ink-900" /> to accounts to pay. Your sign-off goes on the audit trail.</>}
            confirmLabel="Sign off" pending={signOff.isPending} error={signOff.error} onConfirm={() => signOff.mutate()} />
          <Confirm open={dialog === 'void'} onClose={closeDialog} title={`Void ${bill.number}?`}
            body={bill.status === 'APPROVED' ? 'The approval journal is reversed in an open period. The number is kept and the bill is marked void.' : 'The draft is marked void. Nothing was posted to the ledger.'}
            confirmLabel="Void bill" reasonLabel="Reason (required — goes on the audit trail)" pending={voidBill.isPending} error={voidBill.error} onConfirm={(r) => voidBill.mutate(r)} />
        </>
      )}
    </div>
  );
}

// ─── shared pieces ────────────────────────────────────────────────────────────

/** The meta card: details on the left, the balance on the right (as on the invoice page). */
function MetaCard({ rows, currency, balanceMinor, totalMinor, paidMinor, draft = false }: { rows: Array<{ label: string; value: ReactNode }>; currency: string; balanceMinor: number; totalMinor: number; paidMinor: number; draft?: boolean }) {
  return (
    <div className="surface grid sm:grid-cols-5">
      <dl className="space-y-2.5 px-5 py-4 text-[13px] sm:col-span-3">
        {rows.map(({ label, value }) => (
          <div key={label} className="flex items-center gap-3">
            <dt className="w-28 shrink-0 text-ink-500">{label}</dt>
            <dd className="min-w-0 flex-1 text-ink-900">{value}</dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-col justify-center border-t border-ink-100 bg-ink-50/60 px-5 py-4 sm:col-span-2 sm:border-l sm:border-t-0">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500">Balance due</p>
        <Money exact minor={balanceMinor} currency={currency} className={`text-[20px] font-semibold tabular-nums ${balanceMinor > 0 || draft ? 'text-ink-900' : 'text-emerald-700'}`} />
        <div className="mt-3 grid grid-cols-2 gap-2 border-t border-ink-100 pt-3 text-[12px]">
          <div><p className="text-ink-500">Total</p><Money exact minor={totalMinor} currency={currency} className="font-medium text-ink-800" /></div>
          <div><p className="text-ink-500">Paid</p><Money exact minor={paidMinor} currency={currency} className="font-medium text-ink-800" /></div>
        </div>
      </div>
    </div>
  );
}

function VatSummaryTable({ lines, currency }: { lines: Array<Pick<BillLine, 'taxCode' | 'taxRate' | 'netMinor' | 'taxMinor'>>; currency: string }) {
  if (!lines.length) return null;
  return (
    <div className="mt-4 px-5">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500">VAT summary by rate</p>
      <table className="w-full max-w-[360px] text-[13px]">
        <thead><tr className="border-b border-ink-100 text-left text-[11.5px] uppercase tracking-wide text-ink-500">
          <th className="py-1.5 font-medium">Rate</th><th className="py-1.5 text-right font-medium">Net</th><th className="py-1.5 text-right font-medium">VAT</th>
        </tr></thead>
        <tbody>{vatSummary(lines).map((r) => (
          <tr key={`${r.code}|${r.rate}`} className="border-b border-ink-50">
            <td className="py-1"><span className="font-mono text-[11.5px]">{r.code}</span> <span className="text-ink-500">{pct(r.rate)}</span></td>
            <td className="py-1 text-right"><Money exact minor={r.netMinor} currency={currency} /></td>
            <td className="py-1 text-right"><Money exact minor={r.taxMinor} currency={currency} /></td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function TotalRow({ label, minor, currency, strong, span }: { label: string; minor: number; currency: string; strong?: boolean; span: number }) {
  return (
    <tr className={strong ? 'font-semibold text-ink-900' : 'text-ink-500'}>
      <td className="px-3 pt-2 text-right" colSpan={span}>{label}</td>
      <td className="px-5 pt-2 text-right"><Money exact minor={minor} currency={currency} /></td>
    </tr>
  );
}

const IncVatChip = ({ on }: { on: boolean }) => (
  <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-500">{on ? 'Prices include VAT' : 'Prices are net of VAT'}</span>
);

/** A label/value row of the tab forms (the reference layout's two-column table). */
/** One cell of a three-column field grid: label above, value or control below, hint underneath. */
function Row({ label, hint, className = '', children }: { label: string; hint?: ReactNode; className?: string; children: ReactNode }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <dt className="mb-1.5 text-[12.5px] font-bold uppercase tracking-wider text-ink-900">{label}</dt>
      <dd className="min-w-0 text-[13px] text-ink-900">{children}{hint && <p className="mt-1 text-[11px] text-ink-400">{hint}</p>}</dd>
    </div>
  );
}

const none = <span className="text-ink-400">—</span>;
const signoffText = (b: Bill) => (!b.requiresSignoff ? (b.status === 'DRAFT' ? 'Checked on approval' : 'Not needed') : b.signedOffAt ? `Signed off ${formatDate(b.signedOffAt)}` : 'Needed before payment');

/** Who invoiced: the vendor, or (an agent invoice) the agent and what for. */
const PartyName = ({ bill }: { bill: Bill }) => (bill.kind === 'AGENT'
  ? <span className="inline-flex flex-wrap items-center gap-1.5 font-semibold">{bill.agentName ?? 'Agent'} <AgentChip /><span className="font-normal text-ink-500">{FEE[bill.agentFeeType ?? 'LETTING']}</span></span>
  : <span className="font-semibold">{bill.vendorName ?? <span className="font-normal text-ink-400">No vendor</span>}</span>);

const PropertyLink = ({ bill }: { bill: Bill }) => (
  <span><Link to={`/app/properties/${bill.propertyId}`} className="text-bronze-600 hover:underline">{bill.propertyName ?? 'Property'}</Link>
    {[bill.buildingName, bill.unitReference ?? (bill.buildingName ? 'Building & communal' : null)].filter(Boolean).map((x) => <span key={x}> · {x}</span>)}</span>
);

// ─── view mode ────────────────────────────────────────────────────────────────

function BillView({ bill, journalLink, onPay }: { bill: Bill; journalLink: (jid: string | null, label: string) => ReactNode; onPay?: () => void }) {
  const c = bill.currency;
  const lines = bill.lines ?? [];
  const payments = [...(bill.payments ?? [])].reverse();
  const balance = bill.status === 'VOID' ? 0 : bill.outstandingMinor;
  return (
    <div className="space-y-2.5">
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <MetaCard currency={c} balanceMinor={balance} totalMinor={bill.totalMinor} paidMinor={bill.paidMinor} rows={[
            { label: 'Vendor', value: <PartyName bill={bill} /> },
            { label: 'Bill #', value: <span className="font-mono text-[12.5px]">{bill.number}</span> },
            { label: 'Supplier ref', value: bill.supplierReference ?? none },
            { label: 'Bill date', value: <DateText value={bill.billDate} /> },
            { label: 'Due date', value: bill.dueDate ? <span className={bill.isOverdue ? 'font-medium text-red-600' : ''}><DateText value={bill.dueDate} /></span> : none },
            { label: 'Property', value: <PropertyLink bill={bill} /> },
            { label: 'Company', value: bill.entityName ?? '—' },
            ...(bill.workOrderId ? [{ label: 'Work order', value: <Link to={`/app/work-orders/${bill.workOrderId}`} className="text-bronze-600 hover:underline">{bill.workOrderReference ?? 'Work order'}</Link> }] : []),
            ...(bill.tenancyId ? [{ label: 'Tenancy', value: <Link to={`/app/tenancies/${bill.tenancyId}`} className="text-bronze-600 hover:underline">{bill.tenancyReference ?? 'Tenancy'}</Link> }] : []),
          ]} />
        </div>
        <Card title="Status">
          <div className="space-y-3 text-[13px]">
            <div className="flex flex-wrap items-center gap-1.5"><Badge value={bill.status} />{bill.isOverdue && <Badge value="OVERDUE" />}{needsSignoff(bill) && <Badge value="Needs sign-off" tone="warning" />}</div>
            <p className="text-[12px] text-ink-500">Director sign-off: <span className="text-ink-800">{signoffText(bill)}</span></p>
            <p className="text-[12px] text-ink-500">{bill.journalId ? <>Posted to the ledger · {journalLink(bill.journalId, 'view journal')}</> : 'Not posted to the ledger.'}</p>
            {onPay && <Button className="w-full" icon="Banknote" onClick={onPay}>Pay</Button>}
          </div>
        </Card>
      </div>

      <Card title="Lines" actions={<IncVatChip on={bill.pricesIncludeTax} />}>
        <div className="-mx-4 -my-4 overflow-x-auto pb-4">
          <table className="w-full min-w-[760px] text-[13px]">
            <thead><tr className="border-b border-ink-100 bg-ink-50/60 text-left">
              <th className={`${TH} pl-5`}>Description</th>
              <th className={`${TH} text-right`}>Qty</th>
              <th className={`${TH} text-right`}>{bill.pricesIncludeTax ? 'Unit price inc. VAT' : 'Unit price (net)'}</th>
              <th className={TH}>VAT rate</th>
              <th className={`${TH} text-right`}>Net</th>
              <th className={`${TH} text-right`}>VAT</th>
              <th className={`${TH} pr-5 text-right`}>Gross</th>
            </tr></thead>
            <tbody>{lines.map((l) => (
              <tr key={l.id} className="border-b border-ink-100 align-top">
                <td className="px-5 py-2.5 text-ink-700">
                  {l.description}
                  {l.itemCode && <span className="block font-mono text-[10.5px] text-ink-400">{l.itemCode}</span>}
                  {l.accountName && <span className="block text-[11px] text-ink-400">{l.accountCode} · {l.accountName}</span>}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{l.quantity}</td>
                <td className="px-3 py-2.5 text-right"><Money exact minor={l.unitPriceMinor} currency={c} /></td>
                <td className="px-3 py-2.5 text-ink-600"><span className="font-mono text-[11.5px]">{l.taxCode}</span> {pct(l.taxRate)}</td>
                <td className="px-3 py-2.5 text-right"><Money exact minor={l.netMinor} currency={c} /></td>
                <td className="px-3 py-2.5 text-right text-ink-600"><Money exact minor={l.taxMinor} currency={c} /></td>
                <td className="px-5 py-2.5 text-right"><Money exact minor={l.grossMinor} currency={c} /></td>
              </tr>))}
              {lines.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-ink-400">No lines.</td></tr>}
            </tbody>
            <tfoot>
              <TotalRow span={6} label="Subtotal (net)" minor={bill.subtotalMinor} currency={c} />
              <TotalRow span={6} label="VAT" minor={bill.taxMinor} currency={c} />
              <TotalRow span={6} label="Total" minor={bill.totalMinor} currency={c} strong />
            </tfoot>
          </table>
          <VatSummaryTable lines={lines} currency={c} />
        </div>
      </Card>

      <Card title="Ledger" subtitle="Payments made against this bill">
        {!payments.length ? <p className="py-4 text-center text-[12.5px] text-ink-400">{bill.status === 'DRAFT' ? 'Nothing is posted until the bill is approved.' : 'No payments yet.'}</p> : (
          <div className="-mx-4 -my-4 overflow-x-auto">
            <table className="w-full min-w-[600px] text-[13px]">
              <thead><tr className="border-b border-ink-100 bg-ink-50/60 text-left">
                <th className={`${TH} pl-5`}>Date</th><th className={TH}>Type</th><th className={TH}>Reference</th><th className={TH}>Method</th>
                <th className={`${TH} text-right`}>Amount</th><th className={`${TH} pr-5`}>Status</th>
              </tr></thead>
              <tbody>{payments.map((p) => (
                <tr key={p.id} className="border-b border-ink-100 last:border-0">
                  <td className="whitespace-nowrap px-5 py-2.5">{p.paidOn ? formatDate(p.paidOn) : '—'}</td>
                  <td className="px-3 py-2.5">Payment</td>
                  <td className="px-3 py-2.5 font-mono text-[12px] text-ink-700">{p.reference}{p.externalReference && <span className="block font-sans text-[11px] text-ink-400">{p.externalReference}</span>}</td>
                  <td className="px-3 py-2.5 text-ink-600">{methodLabel(p.method)}</td>
                  <td className="px-3 py-2.5 text-right font-medium"><Money exact minor={p.amountMinor} currency={p.currency} /></td>
                  <td className="px-5 py-2.5"><Badge value={p.status} /></td>
                </tr>))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {bill.notes && <Card title="Notes"><p className="whitespace-pre-wrap text-[13px] text-ink-600">{bill.notes}</p></Card>}
    </div>
  );
}

// ─── payments tab ─────────────────────────────────────────────────────────────

/** Debounce typed input so the funding preview is asked once the user pauses. */
function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function SplitRow({ label, hint, minor, currency, warn }: { label: string; hint?: string; minor: number; currency: string; warn?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 px-4 py-2.5">
      <dt className="min-w-0">
        <span className={`block ${warn ? 'font-medium text-amber-800' : 'text-ink-700'}`}>{label}</span>
        {hint && <span className="block text-[11.5px] text-ink-400">{hint}</span>}
      </dt>
      <dd className={`shrink-0 ${minor === 0 ? 'text-ink-300' : 'text-ink-900'}`}><Money exact minor={minor} currency={currency} /></dd>
    </div>
  );
}

/**
 * Pay the bill (while it is payable) and the payments made. Nothing here decides the funding split: every change of
 * amount or the deposit option re-asks POST /bills/:id/pay-preview, and Pay sends the same inputs to /pay. A CLEARED
 * payment can be reversed (bills.void): its journals are reversed and the amount is owed on the bill again.
 */
function PaymentsTab({ bill, payable, canPay, focusToken, journalLink, onChanged }: {
  bill: Bill; payable: boolean; canPay: boolean; focusToken: number;
  journalLink: (jid: string | null, label: string) => ReactNode; onChanged: (msg: string) => void;
}) {
  const canUseDeposits = useCan('bills.payFromDeposit');
  const canReverse = useCan('bills.void');
  const canBanks = useCan('banking.read');
  const c = bill.currency;
  const blank = () => ({ paidOn: today(), amount: (bill.outstandingMinor / 100).toFixed(2), method: 'BANK_TRANSFER', externalReference: '', bankAccountId: '', useDeposits: false, note: '' });
  const [form, setForm] = useState(blank);
  const amountRef = useRef<HTMLInputElement>(null);
  useEffect(() => { amountRef.current?.focus(); amountRef.current?.select(); }, [focusToken]);

  const banks = useQuery({
    queryKey: ['/bank-accounts', bill.entityId],
    queryFn: async () => (await api.get<BankAccount[]>('/bank-accounts', { entityId: bill.entityId })).data,
    enabled: payable && canPay && canBanks, retry: false,
  });
  const bankOptions = (banks.data ?? []).filter((b) => b.isActive && b.glAccountId && b.entityId === bill.entityId);

  const typedMinor = toMinor(form.amount);
  const amountError = typedMinor === null || typedMinor <= 0 ? 'Enter an amount above zero.'
    : typedMinor > bill.outstandingMinor ? `More than the ${fmt(bill.outstandingMinor, c)} outstanding.` : null;
  const debounced = useDebounced(typedMinor);
  const amountMinor = debounced ?? undefined;
  const amountValid = amountMinor !== undefined && amountMinor > 0 && amountMinor <= bill.outstandingMinor;
  const useDeposits = canUseDeposits ? form.useDeposits : undefined;

  const preview = useQuery({
    queryKey: ['bill-pay-preview', bill.id, amountMinor, useDeposits, bill.updatedAt],
    queryFn: () => billsApi.payPreview(bill.id, { amountMinor, useDeposits }),
    enabled: payable && canPay && amountValid,
    retry: false, staleTime: 0, placeholderData: keepPreviousData,
  });
  const split = preview.data;

  const pay = useMutation({
    mutationFn: async () => (await billsApi.pay(bill.id, {
      paidOn: form.paidOn, amountMinor: typedMinor ?? undefined, useDeposits, note: form.note.trim() || undefined,
      method: form.method, externalReference: form.externalReference.trim() || null, bankAccountId: form.bankAccountId || undefined,
    })).data,
    onSuccess: (r) => {
      const s = r.split;
      const parts = [`${fmt(s.fromPropertyMinor, s.currency)} from the property`];
      if (s.fromDepositMinor > 0) parts.push(`${fmt(s.fromDepositMinor, s.currency)} from tenant deposits`);
      if (s.fromCompanyMinor > 0) parts.push(`${fmt(s.fromCompanyMinor, s.currency)} advanced by ${s.opcoEntityName ?? 'the company'}`);
      setForm(blank());
      onChanged(`Paid ${fmt(s.amountMinor, s.currency)} on ${bill.number}: ${parts.join(', ')}.`);
    },
  });

  const blockedBySignoff = !!split && split.requiresSignoff && !split.signedOff && !split.canSignOff;
  const stale = typedMinor !== split?.amountMinor || amountMinor !== split?.amountMinor || (canUseDeposits && form.useDeposits !== split?.useDeposits);
  const canConfirm = !amountError && !!form.paidOn && !!split && !preview.isFetching && !stale && !blockedBySignoff && !pay.isPending;

  const [reversing, setReversing] = useState<BillPayment | null>(null);
  const reverse = useMutation({
    mutationFn: async ({ p, reason }: { p: BillPayment; reason: string }) => billsApi.reversePayment(bill.id, p.id, reason),
    onSuccess: (_d, v) => { setReversing(null); onChanged(`Payment ${v.p.reference} reversed — ${fmt(v.p.amountMinor, v.p.currency)} is owed on the bill again.`); },
  });
  const payments = [...(bill.payments ?? [])].reverse();

  return (
    <div className="space-y-2.5">
      {payable && canPay && (
        <Card>
          <form className="p-5" onSubmit={(e) => { e.preventDefault(); if (canConfirm) pay.mutate(); }}>
            <dl className="grid gap-x-6 gap-y-5 sm:grid-cols-3">
              <Row label="Date paid"><Input aria-label="Date paid" type="date" required value={form.paidOn} max={today()} onChange={(e) => setForm({ ...form, paidOn: e.target.value })} className="w-full" /></Row>
              <Row label={`Amount (${c})`} hint={form.amount && amountError ? <span className="text-red-600">{amountError}</span> : 'Defaults to the full outstanding amount.'}>
                <Input ref={amountRef} aria-label="Amount" type="text" inputMode="decimal" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full" />
              </Row>
              <Row label="Method"><Select aria-label="Method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="w-full">
                {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </Select></Row>
              <Row label="Reference">
                <Input aria-label="Reference" maxLength={80} value={form.externalReference} placeholder="e.g. BACS 40-11-22 or cheque 000123" onChange={(e) => setForm({ ...form, externalReference: e.target.value })} className="w-full" />
              </Row>
              <Row label="Bank account" hint={canBanks ? undefined : "Paid from the company's main bank account."}>
                {canBanks
                  ? <Select aria-label="Bank account" value={form.bankAccountId} onChange={(e) => setForm({ ...form, bankAccountId: e.target.value })} className="w-full">
                      <option value="">{bill.entityName ? `${bill.entityName}'s main bank account` : "The company's main bank account"}</option>
                      {bankOptions.map((b) => <option key={b.id} value={b.id}>{[b.name, b.bankName].filter(Boolean).join(' · ') || 'Bank account'}</option>)}
                    </Select>
                  : <span className="text-ink-600">Main bank account</span>}
              </Row>
              <Row label="Note"><Textarea aria-label="Note" rows={1} maxLength={2000} value={form.note} placeholder="Optional, kept on the payment" onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full" /></Row>
              {canUseDeposits && (
                <Row label="Tenant deposits" className="sm:col-span-3">
                  <label className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/70 px-3.5 py-3 text-[12.5px] text-amber-900">
                    <input type="checkbox" className="mt-0.5" checked={form.useDeposits} onChange={(e) => setForm({ ...form, useDeposits: e.target.checked })} />
                    <span>
                      <span className="font-medium">Use tenant deposit money</span>
                      <span className="mt-0.5 block text-amber-800">This is tenants' money held against their tenancies, not the property's. Only use it if the shortfall cannot wait for the group company or the next rent.</span>
                    </span>
                  </label>
                </Row>
              )}
              <Row label="How it is funded" className="sm:col-span-3">
                <div className="rounded-lg border border-ink-200">
                  <div className="flex items-center justify-between border-b border-ink-100 px-4 py-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">Funding split</span>
                    {preview.isFetching && <Loader2 className="h-4 w-4 animate-spin text-bronze-400" />}
                  </div>
                  {!amountValid ? <p className="px-4 py-3 text-[12.5px] text-ink-400">Enter an amount to see the split.</p>
                    : !split ? (preview.isError ? <div className="p-4"><FormError error={preview.error} /></div> : <p className="px-4 py-3 text-[12.5px] text-ink-400">Working out the split…</p>)
                    : (
                      <dl className="divide-y divide-ink-100 text-[13px]">
                        <SplitRow label="From property balance" hint={`${split.propertyName} has ${fmt(split.availableForBillMinor, split.currency)} available for this bill`} minor={split.fromPropertyMinor} currency={split.currency} />
                        {(canUseDeposits || split.fromDepositMinor > 0) && (
                          <SplitRow label="From tenant deposits" hint={`${fmt(split.balance.depositsMinor, split.currency)} of deposits held${form.useDeposits ? '' : ' — not used'}`} minor={split.fromDepositMinor} currency={split.currency} warn={split.fromDepositMinor > 0} />
                        )}
                        <SplitRow
                          label={split.sameEntity ? `Paid by ${split.balance.entityName ?? 'the company'} (property goes overdrawn)` : `Advanced by ${split.opcoEntityName ?? 'the group company'}`}
                          hint={split.fromCompanyMinor > 0 && !split.sameEntity ? 'Recovered automatically from the next rent on the property' : undefined}
                          minor={split.fromCompanyMinor} currency={split.currency} warn={split.fromCompanyMinor > 0} />
                        <div className="flex items-center justify-between bg-ink-50/60 px-4 py-2 font-semibold text-ink-900">
                          <dt>Total</dt><dd><Money exact minor={split.amountMinor} currency={split.currency} /></dd>
                        </div>
                      </dl>
                    )}
                </div>
                {split && (
                  <div className={`mt-2 flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-[12.5px] ${blockedBySignoff ? 'border-red-200 bg-red-50 text-red-800' : 'border-ink-200 bg-ink-50/60 text-ink-700'}`}>
                    {split.requiresSignoff && !split.signedOff ? <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> : <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />}
                    <div>
                      <p><span className="font-medium">Needs director sign-off:</span> {split.requiresSignoff ? 'Yes' : 'No'} <span className="text-ink-500">(limit {fmt(split.signoffLimitMinor, split.currency)})</span></p>
                      {split.requiresSignoff && split.signedOff && <p className="mt-0.5">Already signed off.</p>}
                      {split.requiresSignoff && !split.signedOff && split.canSignOff && <p className="mt-0.5">You can sign off — paying records your sign-off.</p>}
                      {blockedBySignoff && <p className="mt-0.5 font-medium">A director (a user who can approve bills) must sign this off. You cannot pay it yet.</p>}
                    </div>
                  </div>
                )}
                {split && split.warnings.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {split.warnings.map((w) => <li key={w} className="flex items-start gap-2 text-[12px] text-ink-600"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />{w}</li>)}
                  </ul>
                )}
              </Row>
              <div className="flex items-end sm:col-start-3">
                <Button type="submit" icon="Banknote" className="w-full justify-center" disabled={!canConfirm}>{pay.isPending ? 'Paying…' : 'Pay bill'}</Button>
              </div>
            </dl>
            {!!pay.error && <div className="mt-4"><FormError error={pay.error} /></div>}
          </form>
        </Card>
      )}
      {!payable && (bill.status === 'DRAFT' || bill.status === 'VOID') && (
        <p className="rounded-lg bg-ink-50 px-3.5 py-2.5 text-[12.5px] text-ink-500">
          {bill.status === 'DRAFT' ? 'Approve the bill before paying it.' : 'This bill is void.'}
        </p>
      )}
      {payable && !canPay && <p className="rounded-lg bg-ink-50 px-3.5 py-2.5 text-[12.5px] text-ink-500">You cannot pay bills.</p>}

      <Card>
        {!payments.length ? <p className="py-4 text-center text-[12.5px] text-ink-400">No payments made yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-[13px]">
              <thead><tr className="border-b border-ink-100 bg-ink-50/60 text-left">
                <th className={`${TH} pl-5`}>Date</th><th className={TH}>Reference</th><th className={TH}>Method</th>
                <th className={`${TH} text-right`}>Amount</th><th className={TH}>Funded from</th><th className={TH}>Status</th><th className="w-12 pr-5" />
              </tr></thead>
              <tbody>{payments.map((p) => {
                const actions = canReverse && p.status === 'CLEARED' ? [{ label: 'Reverse', icon: 'Undo2', danger: true, onClick: () => setReversing(p) }] : [];
                return (
                  <tr key={p.id} className="border-b border-ink-100 align-top last:border-0">
                    <td className="whitespace-nowrap px-5 py-2.5">{p.paidOn ? formatDate(p.paidOn) : '—'}</td>
                    <td className="px-3 py-2.5">
                      <span className="font-mono text-[12px] text-ink-700">{p.reference}</span>
                      {p.externalReference && <span className="block text-[11px] text-ink-500">{p.externalReference}</span>}
                      <span className="block whitespace-nowrap text-[11px] text-ink-400">
                        {p.spvJournalId && journalLink(p.spvJournalId, 'journal')}
                        {p.opcoJournalId && <> · {journalLink(p.opcoJournalId, 'group journal')}</>}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-ink-600">{methodLabel(p.method)}</td>
                    <td className="px-3 py-2.5 text-right font-medium"><Money exact minor={p.amountMinor} currency={p.currency} /></td>
                    <td className="px-3 py-2.5 text-[12px] text-ink-600">
                      {p.fromPropertyMinor > 0 && <span className="block">Property <Money exact minor={p.fromPropertyMinor} currency={p.currency} /></span>}
                      {p.fromDepositMinor > 0 && <span className="block text-amber-700">Deposits <Money exact minor={p.fromDepositMinor} currency={p.currency} /></span>}
                      {p.fromCompanyMinor > 0 && <span className="block text-amber-700">Group advance <Money exact minor={p.fromCompanyMinor} currency={p.currency} /></span>}
                    </td>
                    <td className="px-3 py-2.5"><Badge value={p.status} />{p.status === 'REVERSED' && p.reversalReason && <span className="mt-0.5 block max-w-[200px] text-[11px] text-ink-400">{p.reversalReason}</span>}</td>
                    <td className="py-2 pr-5 text-right">{actions.length > 0 && <ActionMenu vertical label={`Actions for ${p.reference}`} items={actions} />}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </Card>

      {reversing && (
        <Confirm open onClose={() => { setReversing(null); reverse.reset(); }} title={`Reverse ${reversing.reference}?`}
          body={`The money did not leave, or went to the wrong place: reverses the payment's journals, cancels any group advance it raised, and ${fmt(reversing.amountMinor, reversing.currency)} is owed on the bill again. This cannot be undone.`}
          confirmLabel="Reverse payment" reasonLabel="Reason (required — goes on the audit trail)"
          pending={reverse.isPending} error={reverse.error} onConfirm={(reason) => reverse.mutate({ p: reversing, reason })} />
      )}
    </div>
  );
}

// ─── where: building and unit pickers ─────────────────────────────────────────

/** The property's buildings (GET /bills/locations — finance need not hold buildings.read). */
function useBuildings(propertyId: string) {
  return useQuery({
    queryKey: ['/bills/locations', propertyId],
    queryFn: async () => (await api.get<BillLocations>('/bills/locations', { propertyId })).data.buildings,
    enabled: !!propertyId,
  });
}

function BuildingSelect({ propertyId, value, onChange, className = 'w-full' }: { propertyId: string; value: string; onChange: (id: string) => void; className?: string }) {
  const buildings = useBuildings(propertyId);
  return (
    <Select aria-label="Building" value={value} onChange={(e) => onChange(e.target.value)} className={className} disabled={!propertyId}
      title={propertyId && buildings.data && !buildings.data.length ? 'This property has no buildings set up' : undefined}>
      <option value="">{propertyId ? 'Whole property' : 'Choose a property first'}</option>
      {buildings.data?.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
    </Select>
  );
}

function UnitSelect({ propertyId, buildingId, value, onChange }: { propertyId: string; buildingId: string; value: UnitOption | null; onChange: (u: UnitOption | null) => void }) {
  const searchUnits = async (q: string) => (await api.get<UnitOption[]>('/units', { propertyId, buildingId: buildingId || undefined, search: q || undefined, perPage: 20 })).data;
  return (
    <SearchSelect<UnitOption> aria-label="Unit" value={value} onChange={onChange} loadOptions={searchUnits} reloadKey={`${propertyId}|${buildingId}`}
      getOptionKey={(u) => u.id} getOptionLabel={(u) => u.reference}
      renderOption={(u) => (
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-ink-900">{u.reference}{u.name ? <span className="text-ink-500"> · {u.name}</span> : null}</span>
          {u.building?.name && <span className="shrink-0 truncate text-[11.5px] text-ink-400">{u.building.name}</span>}
        </div>
      )}
      placeholder={propertyId ? 'Building & communal' : 'Choose a property first'} disabled={!propertyId} />
  );
}

const searchVendors = async (q: string) => (await api.get<Vendor[]>('/vendors', { search: q || undefined, perPage: 20 })).data;
const vendorLabel = (v: Vendor) => (v.trade ? `${v.name} — ${v.trade}` : v.name);
function VendorSelect({ value, onChange }: { value: Vendor | null; onChange: (v: Vendor | null) => void }) {
  return <SearchSelect<Vendor> aria-label="Vendor" value={value} onChange={onChange} loadOptions={searchVendors} getOptionKey={(v) => v.id} getOptionLabel={vendorLabel} placeholder="Search vendors (optional)" />;
}

const savedVendor = (b: Bill): Vendor | null => (b.vendorId ? { id: b.vendorId, name: b.vendorName ?? 'Vendor' } : null);
const savedUnit = (b: Bill): UnitOption | null => (b.unitId ? { id: b.unitId, reference: b.unitReference ?? 'Unit', buildingId: b.buildingId } : null);

// ─── details tab ──────────────────────────────────────────────────────────────

/** The bill's fields. A draft's vendor, location, dates and supplier reference can be changed here; an approved bill is read-only. */
function DetailsTab({ bill, canEdit, journalLink, onSaved }: { bill: Bill; canEdit: boolean; journalLink: (jid: string | null, label: string) => ReactNode; onSaved: (msg: string) => void }) {
  const editable = bill.status === 'DRAFT' && canEdit;
  const [form, setForm] = useState({ billDate: bill.billDate ?? today(), dueDate: bill.dueDate ?? '', supplierReference: bill.supplierReference ?? '', buildingId: bill.buildingId ?? '' });
  const [vendor, setVendor] = useState<Vendor | null>(() => savedVendor(bill));
  const [unit, setUnit] = useState<UnitOption | null>(() => savedUnit(bill));

  const changed = {
    vendor: (vendor?.id ?? null) !== bill.vendorId,
    where: (unit?.id ?? null) !== bill.unitId || (form.buildingId || null) !== bill.buildingId,
    billDate: form.billDate !== bill.billDate,
    dueDate: (form.dueDate || null) !== bill.dueDate,
    supplierReference: (form.supplierReference.trim() || null) !== bill.supplierReference,
  };
  const dirty = Object.values(changed).some(Boolean);
  const datesOk = !!form.billDate && (!form.dueDate || form.dueDate >= form.billDate);
  const save = useMutation({
    mutationFn: async () => api.patch(`/bills/${bill.id}`, {
      ...(changed.vendor ? { vendorId: vendor?.id ?? null } : {}),
      ...(changed.where ? { unitId: unit?.id ?? null, buildingId: form.buildingId || null } : {}),
      ...(changed.billDate ? { billDate: form.billDate } : {}),
      ...(changed.dueDate ? { dueDate: form.dueDate || null } : {}),
      ...(changed.supplierReference ? { supplierReference: form.supplierReference.trim() || null } : {}),
    }),
    onSuccess: () => onSaved('Draft saved.'),
  });

  return (
    <Card>
      <dl className="grid gap-x-6 gap-y-7 p-5 sm:grid-cols-3">
        <Row label="Company">{bill.entityName ?? '—'}</Row>
        <Row label={bill.kind === 'AGENT' ? 'Agent' : 'Vendor'}>{editable && bill.kind !== 'AGENT'
          ? <div><VendorSelect value={vendor} onChange={setVendor} /></div>
          : <PartyName bill={bill} />}</Row>
        <Row label="Property"><Link to={`/app/properties/${bill.propertyId}`} className="text-bronze-600 hover:underline">{bill.propertyName ?? 'Property'}</Link></Row>
        <Row label="Building">{editable
          ? <BuildingSelect propertyId={bill.propertyId} value={form.buildingId} className="w-full"
              onChange={(b) => { setForm({ ...form, buildingId: b }); if (unit && b && unit.buildingId !== b) setUnit(null); }} />
          : bill.buildingName ?? none}</Row>
        <Row label="Unit" hint={editable && !unit ? 'Empty = the building & communal parts' : undefined}>{editable
          ? <div><UnitSelect propertyId={bill.propertyId} buildingId={form.buildingId} value={unit} onChange={(u) => { setUnit(u); if (u?.buildingId) setForm((f) => ({ ...f, buildingId: u.buildingId! })); }} /></div>
          : bill.unitReference ?? <span className="text-ink-400">{bill.buildingName ? 'Building & communal' : 'Whole property'}</span>}</Row>
        <Row label="Bill date">{editable
          ? <Input aria-label="Bill date" type="date" value={form.billDate} onChange={(e) => setForm({ ...form, billDate: e.target.value })} className="w-full" />
          : <DateText value={bill.billDate} />}</Row>
        <Row label="Due date" hint={editable && !datesOk && form.dueDate ? <span className="text-red-600">The due date can't be before the bill date.</span> : undefined}>{editable
          ? <Input aria-label="Due date" type="date" value={form.dueDate} min={form.billDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className="w-full" />
          : bill.dueDate ? <span className={bill.isOverdue ? 'font-medium text-red-600' : ''}><DateText value={bill.dueDate} /></span> : none}</Row>
        <Row label="Supplier ref" hint={editable ? "The supplier's own invoice number." : undefined}>{editable
          ? <Input aria-label="Supplier reference" maxLength={60} value={form.supplierReference} placeholder="e.g. INV-20431" onChange={(e) => setForm({ ...form, supplierReference: e.target.value })} className="w-full" />
          : bill.supplierReference ?? none}</Row>
        <Row label="Bill #"><span className="font-mono text-[12.5px]">{bill.number}</span></Row>
        <Row label="Status" hint="Updates automatically from approval, payments and voids."><span className="inline-flex gap-1.5"><Badge value={bill.status} />{bill.isOverdue && <Badge value="OVERDUE" />}</span></Row>
        <Row label="Currency">{bill.currency}</Row>
        <Row label="Prices">{bill.pricesIncludeTax ? 'Include VAT' : 'Net of VAT'}</Row>
        <Row label="Director sign-off">{signoffText(bill)}</Row>
        {bill.workOrderId && <Row label="Work order"><Link to={`/app/work-orders/${bill.workOrderId}`} className="text-bronze-600 hover:underline">{bill.workOrderReference ?? 'Work order'}</Link></Row>}
        {bill.tenancyId && <Row label="Tenancy"><Link to={`/app/tenancies/${bill.tenancyId}`} className="text-bronze-600 hover:underline">{bill.tenancyReference ?? 'Tenancy'}</Link></Row>}
        {bill.journalId && <Row label="Journal">{journalLink(bill.journalId, 'View the posting')}</Row>}
      </dl>
      {editable && (
        <div className="flex items-center justify-end gap-3 border-t border-ink-100 px-5 py-3">
          <div className="flex-1"><FormError error={save.error} /></div>
          <Button disabled={!dirty || !datesOk || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save changes'}</Button>
        </div>
      )}
    </Card>
  );
}

// ─── notes tab ────────────────────────────────────────────────────────────────

/** Internal notes. They stay editable after approval (they move no money); a void bill is final. */
function NotesTab({ bill, canEdit, onSaved }: { bill: Bill; canEdit: boolean; onSaved: (msg: string) => void }) {
  const [notes, setNotes] = useState(bill.notes ?? '');
  const editable = canEdit && bill.status !== 'VOID';
  const save = useMutation({
    mutationFn: async () => api.patch(`/bills/${bill.id}`, { notes: notes.trim() ? notes : null }),
    onSuccess: () => onSaved('Notes saved.'),
  });
  const dirty = (notes.trim() ? notes : null) !== (bill.notes ?? null);
  return (
    <Card title="Notes" subtitle="Internal to the finance team — never sent to the supplier">
      {editable ? (
        <div className="space-y-3">
          <Textarea aria-label="Notes" rows={6} maxLength={4000} value={notes} placeholder="e.g. what this bill covers, or who agreed the price" onChange={(e) => setNotes(e.target.value)} className="w-full" />
          <div className="flex items-center justify-end gap-3">
            <div className="flex-1"><FormError error={save.error} /></div>
            <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save notes'}</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {bill.notes ? <p className="whitespace-pre-wrap text-[13px] text-ink-700">{bill.notes}</p> : <p className="text-[13px] text-ink-400">No notes.</p>}
          <p className="text-[11.5px] text-ink-400">{bill.status === 'VOID' ? 'A void bill cannot be changed.' : 'You cannot edit bills.'}</p>
        </div>
      )}
    </Card>
  );
}

// ─── edit mode (new, or a draft) ──────────────────────────────────────────────

/**
 * One line being edited. descriptionTouched: typed by hand, so picking an item never overwrites it. rateTouched: chosen
 * by hand, so the line stops following the region's default. Picking an item always re-fills price and rate.
 */
interface Line {
  key: number;
  item: Item | null;
  description: string;
  descriptionTouched: boolean;
  quantity: string;
  /** Major units, VAT included. */
  unitPrice: string;
  /** '' = follow the region's default code (a line with no item whose rate nobody chose). */
  taxCode: string;
  rateTouched: boolean;
  /** A saved line's ledger account, kept when the line is re-saved. */
  accountId: string | null;
}

let nextKey = 1;
const blankLine = (): Line => ({ key: nextKey++, item: null, description: '', descriptionTouched: false, quantity: '1', unitPrice: '', taxCode: '', rateTouched: false, accountId: null });

/** A saved draft line as an editable one. Net-priced drafts (work orders, agents) are shown at their gross unit price. */
function lineFromSaved(l: BillLine, inclusive: boolean, currency: string, region: 'UK' | 'AE'): Line {
  const unitGross = inclusive ? l.unitPriceMinor : Math.round(l.grossMinor / (l.quantity || 1));
  const item = l.itemId ? ({ id: l.itemId, code: l.itemCode ?? '', name: l.description, unitPriceMinor: 0, currency, pricesIncludeTax: true, taxCode: l.taxCode ?? '', taxRatePercent: l.taxRate, region } as Item) : null;
  return { key: nextKey++, item, description: l.description, descriptionTouched: true, quantity: String(l.quantity), unitPrice: (unitGross / 100).toFixed(2), taxCode: l.taxCode ?? '', rateTouched: true, accountId: l.accountId };
}
const qtyOf = (l: Line) => { const n = Number(l.quantity); return Number.isFinite(n) && n > 0 ? n : 1; };

/** An item's price with VAT included, whatever basis the item stores it on. */
function itemGrossMinor(item: Item): number {
  if (item.pricesIncludeTax) return item.unitPriceMinor;
  return item.unitPriceMinor + Math.round((item.unitPriceMinor * (item.taxRatePercent ?? 0)) / 100);
}

/** A line the server will accept: it says what it is (or names an item) and has a positive price (typed, or the item's). */
const lineReady = (l: Line) => {
  const typed = toMinor(l.unitPrice);
  const priced = typed !== null ? typed > 0 : !!l.item && l.item.unitPriceMinor > 0;
  return (!!l.item || l.description.trim() !== '') && priced;
};

// "Standard rate (20%)" already carries its percentage, so the option reads "Standard rate · 20%" and fits the select.
const rateOptionLabel = (label: string, ratePercent: number) => `${label.replace(/\s*\([^)]*%\)\s*$/, '')} · ${pct(ratePercent)}`;

/** A file waiting to go up with the new bill. */
interface Attachment { key: number; file: File; category: DocumentCategory; state: 'ready' | 'uploading' | 'done' | 'error'; error?: string }
const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,image/heic';
const acceptable = (f: File) => f.type === 'application/pdf' || ['image/jpeg', 'image/png', 'image/webp', 'image/heic'].includes(f.type);
const fileOk = (f: File) => acceptable(f) && f.size <= MAX_UPLOAD_BYTES;
let nextFileKey = 1;

/**
 * The bill edited in place: a new one (POST /bills, then its attachments go up against it) or a draft (PATCH; the
 * lines are only re-sent when they were changed, so an untouched work-order or agent draft keeps its net pricing).
 */
function BillEditor({ bill, workOrderId, onCreated, onSaved }: { bill: Bill | null; workOrderId: string | null; onCreated: (b: Bill, msg?: string) => void; onSaved: (msg: string) => void }) {
  const [form, setForm] = useState({ propertyId: bill?.propertyId ?? '', buildingId: bill?.buildingId ?? '', billDate: bill?.billDate ?? today(), dueDate: bill?.dueDate ?? '', supplierReference: bill?.supplierReference ?? '', notes: bill?.notes ?? '' });
  const [vendor, setVendor] = useState<Vendor | null>(() => (bill ? savedVendor(bill) : null));
  const [unit, setUnit] = useState<UnitOption | null>(() => (bill ? savedUnit(bill) : null));
  const [lines, setLines] = useState<Line[]>(() => (bill?.lines?.length ? bill.lines.map((l) => lineFromSaved(l, bill.pricesIncludeTax, bill.currency, bill.region)) : [blankLine()]));
  const [linesTouched, setLinesTouched] = useState(!bill);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [dragging, setDragging] = useState(false);
  const canBillsUpdate = useCan('bills.update');
  const canDocsCreate = useCan('documents.create');
  const canAttach = !bill && (canBillsUpdate || canDocsCreate);

  const properties = useQuery({ queryKey: ['properties-picker'], queryFn: async () => (await api.get<Property[]>('/properties', { perPage: 200 })).data, enabled: !bill });
  const entities = useQuery({ queryKey: ['entities'], queryFn: async () => (await api.get<Entity[]>('/entities')).data, enabled: !bill, retry: false });
  const property = properties.data?.find((p) => p.id === form.propertyId);
  const pickProperty = (propertyId: string) => { setForm((f) => ({ ...f, propertyId, buildingId: '' })); setUnit(null); };
  const pickBuilding = (buildingId: string) => { setForm((f) => ({ ...f, buildingId })); if (unit && buildingId && unit.buildingId !== buildingId) setUnit(null); };
  const pickUnit = (u: UnitOption | null) => { setUnit(u); if (u?.buildingId) setForm((f) => ({ ...f, buildingId: u.buildingId! })); };
  // A bill sits in its property's company and is priced from that company's region.
  const region = bill?.region ?? property?.region ?? 'UK';
  const entityId = bill?.entityId ?? property?.entityId ?? undefined;
  const companyName = bill?.entityName ?? entities.data?.find((e) => e.id === entityId)?.name;

  const rates = useQuery({ queryKey: ['/tax-rates', region, false], queryFn: async () => (await api.get<TaxRate[]>('/tax-rates', { region })).data });
  const defaults = useQuery({ queryKey: ['/tax-rates/defaults', region], queryFn: async () => (await api.get<TaxDefaults>('/tax-rates/defaults', { region })).data });
  const inForce = useMemo(() => {
    const byCode = new Map<string, TaxRate>();
    for (const r of rates.data ?? []) if (r.inForce && !byCode.has(r.code)) byCode.set(r.code, r);
    return [...byCode.values()].sort((a, b) => b.ratePercent - a.ratePercent || a.code.localeCompare(b.code));
  }, [rates.data]);
  const rateOf = (code: string) => inForce.find((r) => r.code === code);
  // Bills have no invoice type: a line with no item and a rate nobody chose takes the region's default rate.
  const defaultCode = defaults.data?.fallbackCode ?? '';
  const codeOf = (l: Line) => l.taxCode || defaultCode;

  const editLines = (fn: (ls: Line[]) => Line[]) => { setLinesTouched(true); setLines(fn); };
  const setLine = (key: number, patch: Partial<Line>) => editLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  /** Picking an item fills its description (unless typed by hand), its price (when it has one: system items are 0) and its VAT code. */
  const pickItem = (key: number, item: Item | null) => editLines((ls) => ls.map((l) => {
    if (l.key !== key) return l;
    if (!item) return { ...l, item: null, taxCode: l.rateTouched ? l.taxCode : '' };
    return {
      ...l, item,
      description: l.descriptionTouched ? l.description : item.name,
      unitPrice: item.unitPriceMinor > 0 ? (itemGrossMinor(item) / 100).toFixed(2) : l.unitPrice,
      taxCode: item.taxCode, rateTouched: false,
    };
  }));

  const linesBody = () => lines.filter(lineReady).map((l) => {
    const price = toMinor(l.unitPrice);
    return { itemId: l.item?.id ?? undefined, description: l.description.trim() || undefined, quantity: qtyOf(l), unitPriceMinor: price ?? undefined, taxCode: codeOf(l) || undefined, ...(l.accountId ? { accountId: l.accountId } : {}) };
  });
  const body = (): CreateBillBody => ({
    propertyId: form.propertyId, buildingId: form.buildingId || null, unitId: unit?.id ?? null, vendorId: vendor?.id ?? null,
    billDate: form.billDate, dueDate: form.dueDate || null, supplierReference: form.supplierReference.trim() || null,
    notes: form.notes.trim() || null, pricesIncludeTax: true, lines: linesBody(),
    ...(workOrderId ? { workOrderId } : {}),
  });
  const datesOk = !!form.billDate && (!form.dueDate || form.dueDate >= form.billDate);
  const pricesOk = lines.some(lineReady) && lines.every((l) => l.unitPrice.trim() === '' || (toMinor(l.unitPrice) ?? -1) > 0);
  const ready = !!form.propertyId && datesOk && pricesOk;

  // Server-exact totals: the preview of the same body, debounced. An untouched draft shows its own saved figures.
  const savedFigures = !!bill && !linesTouched;
  const bodyKey = JSON.stringify(ready && !savedFigures ? body() : null);
  const canSave = ready && files.every((f) => fileOk(f.file));
  const [debouncedKey, setDebouncedKey] = useState(bodyKey);
  useEffect(() => { const t = setTimeout(() => setDebouncedKey(bodyKey), 300); return () => clearTimeout(t); }, [bodyKey]);
  const preview = useQuery({
    queryKey: ['bill-preview', debouncedKey],
    queryFn: async () => (await api.post<BillPreview>('/bills/preview', JSON.parse(debouncedKey))).data,
    enabled: debouncedKey !== 'null',
    retry: false,
    staleTime: 60_000,
  });
  const exact = savedFigures || (!!preview.data && !preview.isFetching && debouncedKey === bodyKey);
  const currency = bill?.currency ?? preview.data?.currency ?? (region === 'AE' ? 'AED' : 'GBP');

  // Local estimate while the preview is on its way (the server's figures replace it).
  const local = useMemo(() => {
    let net = 0, tax = 0, total = 0;
    for (const l of lines.filter(lineReady)) {
      const price = toMinor(l.unitPrice) ?? (l.item ? itemGrossMinor(l.item) : 0);
      const gross = Math.round(price * qtyOf(l));
      const def = rateOf(codeOf(l));
      const rate = def?.taxable === false ? 0 : (def?.ratePercent ?? 0);
      const n = Math.round((gross * 100) / (100 + rate));
      net += n; tax += gross - n; total += gross;
    }
    return { subtotalMinor: net, taxMinor: tax, totalMinor: total };
  }, [lines, inForce, defaultCode]); // eslint-disable-line react-hooks/exhaustive-deps
  const totals = savedFigures ? bill! : exact ? preview.data! : local;
  const exactLines = savedFigures ? bill!.lines ?? [] : exact ? preview.data!.lines : null;

  // Per line: the server's figures when current, else qty × price for the gross.
  const readyKeys = lines.filter(lineReady).map((l) => l.key);
  const exactLine = (l: Line) => { const i = savedFigures ? lines.indexOf(l) : readyKeys.indexOf(l.key); return exactLines && i >= 0 ? exactLines[i] ?? null : null; };
  const lineGross = (l: Line) => {
    const p = exactLine(l);
    if (p) return p.grossMinor;
    const price = toMinor(l.unitPrice) ?? (l.item ? itemGrossMinor(l.item) : null);
    return price === null ? null : Math.round(price * qtyOf(l));
  };

  const searchItems = async (q: string) => (await api.get<Item[]>('/items', { q, region, entityId, perPage: 20 })).data;

  const addFiles = (list: FileList | File[]) => {
    const picked = [...list].map((file): Attachment => {
      const error = !acceptable(file) ? 'Not a PDF or an image (JPEG, PNG, WebP, HEIC)' : file.size > MAX_UPLOAD_BYTES ? `Larger than ${MAX_UPLOAD_BYTES / 1024 / 1024} MB` : undefined;
      return { key: nextFileKey++, file, category: 'INVOICE', state: error ? 'error' : 'ready', error };
    });
    setFiles((fs) => [...fs, ...picked]);
  };
  const setFile = (key: number, patch: Partial<Attachment>) => setFiles((fs) => fs.map((f) => (f.key === key ? { ...f, ...patch } : f)));
  /** Upload every file, one after another, against the new bill. The number that failed. */
  const uploadAll = async (b: Bill) => {
    let failed = 0;
    for (const a of files) {
      setFile(a.key, { state: 'uploading', error: undefined });
      try {
        await documentsApi.upload({ resource: 'bill', resourceId: b.id, category: a.category, visibleToTenant: false, file: await toUpload(a.file) });
        setFile(a.key, { state: 'done' });
      } catch (e) {
        failed += 1;
        setFile(a.key, { state: 'error', error: (e as Error).message });
      }
    }
    return failed;
  };

  const save = useMutation({
    mutationFn: async (andApprove: boolean) => {
      let saved: Bill;
      let failed = 0;
      if (!bill) {
        saved = (await api.post<Bill>('/bills', body())).data;
        if (canAttach && files.length) failed = await uploadAll(saved);
      } else {
        saved = (await api.patch<Bill>(`/bills/${bill.id}`, {
          vendorId: vendor?.id ?? null, buildingId: form.buildingId || null, unitId: unit?.id ?? null,
          billDate: form.billDate, dueDate: form.dueDate || null, supplierReference: form.supplierReference.trim() || null,
          notes: form.notes.trim() ? form.notes : null,
          ...(linesTouched ? { pricesIncludeTax: true, lines: linesBody() } : {}),
        })).data;
      }
      if (andApprove) await api.post(`/bills/${saved.id}/approve`, {});
      return { saved, andApprove, failed };
    },
    onSuccess: ({ saved, andApprove, failed }) => {
      const what = andApprove ? `Bill ${saved.number} approved — the liability is posted to the ledger.` : bill ? 'Draft saved.' : `Draft ${saved.number} created.`;
      const msg = failed ? `${what} ${failed} attachment${failed === 1 ? '' : 's'} did not upload — add ${failed === 1 ? 'it' : 'them'} again on the Documents tab.` : what;
      if (!bill) onCreated(saved, msg);
      else onSaved(msg);
    },
  });

  const rateOptions = (code: string) => (
    <>
      {!code && <option value="">Choose…</option>}
      {inForce.map((r) => <option key={r.code} value={r.code}>{rateOptionLabel(r.label, r.ratePercent)}</option>)}
      {code && !rateOf(code) && <option value={code}>{code}</option>}
    </>
  );

  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const addLine = () => {
    editLines((ls) => [...ls, blankLine()]);
    requestAnimationFrame(() => tbodyRef.current?.querySelector<HTMLInputElement>('tr:last-child input[role="combobox"]')?.focus());
  };
  const field = 'w-full';

  return (
    <div className="space-y-2.5">
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <MetaCard draft currency={currency} balanceMinor={totals.totalMinor} totalMinor={totals.totalMinor} paidMinor={0} rows={[
            { label: 'Property', value: bill ? <Link to={`/app/properties/${bill.propertyId}`} className="text-bronze-600 hover:underline">{bill.propertyName}</Link>
              : <Select aria-label="Property" value={form.propertyId} onChange={(e) => pickProperty(e.target.value)} className={field} title="The bill is charged to this property's balance">
                  <option value="">Choose a property…</option>
                  {properties.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select> },
            { label: 'Company', value: companyName ? <span>{companyName}</span> : <span className="text-ink-400">{form.propertyId ? "The property's company" : 'Follows the property'}</span> },
            { label: 'Building', value: <BuildingSelect propertyId={form.propertyId} value={form.buildingId} onChange={pickBuilding} className={field} /> },
            { label: 'Unit', value: <UnitSelect propertyId={form.propertyId} buildingId={form.buildingId} value={unit} onChange={pickUnit} /> },
            { label: bill?.kind === 'AGENT' ? 'Agent' : 'Vendor', value: bill?.kind === 'AGENT' ? <PartyName bill={bill} /> : <VendorSelect value={vendor} onChange={setVendor} /> },
            ...(bill ? [{ label: 'Bill #', value: <span className="font-mono text-[12.5px] text-ink-500">{bill.number}</span> }] : []),
            { label: 'Supplier ref', value: <Input aria-label="Supplier reference" maxLength={60} value={form.supplierReference} placeholder="Their invoice number (optional)" onChange={(e) => setForm({ ...form, supplierReference: e.target.value })} className={field} /> },
            { label: 'Bill date', value: <Input aria-label="Bill date" type="date" value={form.billDate} onChange={(e) => setForm({ ...form, billDate: e.target.value })} className={field} /> },
            { label: 'Due date', value: <div><Input aria-label="Due date" type="date" value={form.dueDate} min={form.billDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className={field} />
              {!datesOk && form.dueDate && <p className="mt-0.5 text-[11px] text-red-600">The due date can't be before the bill date.</p>}</div> },
          ]} />
        </div>
        <Card title="Status">
          <div className="space-y-3 text-[13px]">
            <div className="flex items-center gap-1.5"><Badge value="DRAFT" /><span className="text-ink-500">{bill ? 'Not approved yet' : 'Not saved yet'}</span></div>
            <p className="text-[12px] text-ink-500">Approving posts the liability to the ledger (Dr Repairs &amp; maintenance / Cr Accounts payable). After that it can only be voided.</p>
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" disabled={!canSave || save.isPending} onClick={() => save.mutate(false)}>{save.isPending && !save.variables ? 'Saving…' : 'Save as draft'}</Button>
              <Can do="bills.approve"><Button className="flex-1" disabled={!canSave || save.isPending} onClick={() => save.mutate(true)}>{save.isPending && save.variables ? 'Approving…' : 'Save & approve'}</Button></Can>
            </div>
            <FormError error={save.error} />
          </div>
        </Card>
      </div>

      <Card title="Lines" actions={<div className="flex items-center gap-3"><IncVatChip on={savedFigures ? bill!.pricesIncludeTax : true} /><button type="button" onClick={addLine} className="inline-flex items-center gap-1 text-[12px] text-bronze-600 hover:underline"><Plus className="h-3.5 w-3.5" /> Add line</button></div>}>
        <div className="-mx-4 -my-4 overflow-x-auto pb-4">
          <table className="w-full min-w-[980px] text-[13px]">
            <thead><tr className="border-b border-ink-100 bg-ink-50/60 text-left">
              <th className={`${TH} w-[210px] pl-5`}>Item</th>
              <th className={TH}>Description</th>
              <th className={`${TH} w-[76px] text-right`}>Qty</th>
              <th className={`${TH} w-[120px] whitespace-nowrap text-right`}>Price inc. VAT</th>
              <th className={`${TH} w-[230px]`}>VAT rate</th>
              <th className={`${TH} w-[110px] text-right`}>Gross</th>
              <th className="w-10 pr-5" />
            </tr></thead>
            <tbody ref={tbodyRef}>{lines.map((l, i) => {
              const gross = lineGross(l);
              const p = exactLine(l);
              return (
                <tr key={l.key} className="border-b border-ink-100 align-top">
                  <td className="py-2 pl-5 pr-2">
                    <SearchSelect<Item>
                      aria-label={`Line ${i + 1} item`}
                      value={l.item}
                      onChange={(item) => pickItem(l.key, item)}
                      loadOptions={searchItems}
                      reloadKey={`${region}|${entityId ?? ''}`}
                      getOptionKey={(o) => o.id}
                      getOptionLabel={(o) => `${o.code} · ${o.name}`}
                      renderOption={(o) => (
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="min-w-0"><span className="font-mono text-[11.5px] text-ink-500">{o.code}</span> <span className="text-ink-900">{o.name}</span></div>
                          <div className="shrink-0 text-right text-[12px] tabular-nums text-ink-600">
                            {o.unitPriceMinor > 0 ? formatMoneyExact(itemGrossMinor(o), o.currency) : <span className="text-ink-300">no price</span>}
                            <span className="ml-1.5 text-ink-400">{o.taxRatePercent === null ? o.taxCode : pct(o.taxRatePercent)}</span>
                          </div>
                        </div>
                      )}
                      placeholder="Search items"
                      allowCustom
                      onCustom={(text) => { if (!l.descriptionTouched || !l.description.trim()) setLine(l.key, { description: text, descriptionTouched: true }); }}
                    />
                  </td>
                  <td className="px-2 py-2"><Input aria-label={`Line ${i + 1} description`} value={l.description} placeholder="Description"
                    onChange={(e) => setLine(l.key, { description: e.target.value, descriptionTouched: true })} className="w-full" /></td>
                  <td className="px-2 py-2"><Input aria-label={`Line ${i + 1} quantity`} type="number" min="0" step="0.5" value={l.quantity}
                    onChange={(e) => setLine(l.key, { quantity: e.target.value })} className="w-full text-right" /></td>
                  <td className="px-2 py-2"><Input aria-label={`Line ${i + 1} unit price including VAT`} type="number" min="0" step="0.01" value={l.unitPrice}
                    placeholder={l.item && l.item.unitPriceMinor > 0 ? (itemGrossMinor(l.item) / 100).toFixed(2) : '0.00'}
                    onChange={(e) => setLine(l.key, { unitPrice: e.target.value })} className="w-full text-right" /></td>
                  <td className="px-2 py-2"><Select aria-label={`Line ${i + 1} VAT rate`} value={codeOf(l)} onChange={(e) => setLine(l.key, { taxCode: e.target.value, rateTouched: true })} className="w-full">
                    {rateOptions(codeOf(l))}
                  </Select></td>
                  <td className="px-2 py-2 pt-4 text-right tabular-nums text-ink-800" aria-label={`Line ${i + 1} total`}>
                    {gross === null ? <span className="text-ink-300">—</span> : formatMoneyExact(gross, currency)}
                    {p && <span className="block text-[10.5px] text-ink-400">VAT {formatMoneyExact(p.taxMinor, currency)}</span>}
                  </td>
                  <td className="py-2 pr-5 pt-3.5 text-right">
                    <button type="button" aria-label={`Remove line ${i + 1}`} onClick={() => editLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : [blankLine()]))}
                      className="rounded p-1 text-ink-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              );
            })}</tbody>
            <tfoot aria-live="polite">
              <TotalRow span={5} label="Subtotal (net)" minor={totals.subtotalMinor} currency={currency} />
              <TotalRow span={5} label="VAT" minor={totals.taxMinor} currency={currency} />
              <TotalRow span={5} label="Total" minor={totals.totalMinor} currency={currency} strong />
              <tr><td colSpan={7} className="px-5 pt-1 text-right text-[11px]">
                {!exact && ready && !preview.isError && <span className="text-ink-400">Calculating…</span>}
                {preview.isError && debouncedKey === bodyKey && <span className="text-red-600">{(preview.error as Error).message}</span>}
                {!form.propertyId && <span className="text-ink-400">Choose a property to price the lines.</span>}
              </td></tr>
            </tfoot>
          </table>
          {exactLines && <VatSummaryTable lines={exactLines} currency={currency} />}
          <p className="mt-3 px-5 text-[11px] text-ink-400">Costs go to Repairs &amp; maintenance unless a line names another account.</p>
        </div>
      </Card>

      <Card title="Notes">
        <Textarea aria-label="Notes" rows={3} value={form.notes} placeholder="Internal to the finance team (optional)" onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full" />
      </Card>

      {canAttach && (
        <Card title="Attachments" subtitle="Uploaded once the bill is saved · never shown to a resident">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files); }}
            className={`rounded-lg border px-4 py-4 text-center transition ${dragging ? 'border-bronze-400 bg-bronze-50' : 'border-ink-200 bg-ink-50/40'}`}>
            <Paperclip className="mx-auto h-4 w-4 text-ink-400" />
            <p className="mt-1 text-[12.5px] text-ink-600">
              Drop the supplier&apos;s invoice, a quote or a receipt here, or{' '}
              <label className="cursor-pointer font-medium text-bronze-600 hover:underline">
                choose files
                <input type="file" multiple accept={ACCEPT} className="sr-only" onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }} />
              </label>
            </p>
            <p className="mt-0.5 text-[11px] text-ink-400">PDF or image (JPEG, PNG, WebP, HEIC), up to 7 MB each</p>
          </div>
          {files.length > 0 && (
            <ul className="mt-2 divide-y divide-ink-100 rounded-lg border border-ink-100">
              {files.map((a) => (
                <li key={a.key} className="flex items-center gap-2.5 px-3 py-2 text-[12.5px]">
                  <span className="shrink-0" aria-hidden>
                    {a.state === 'uploading' ? <Loader2 className="h-4 w-4 animate-spin text-bronze-500" />
                      : a.state === 'done' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      : a.state === 'error' ? <AlertCircle className="h-4 w-4 text-red-500" />
                      : <Paperclip className="h-4 w-4 text-ink-400" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-ink-800">{a.file.name}</div>
                    <div className={`truncate text-[11px] ${a.state === 'error' ? 'text-red-600' : 'text-ink-400'}`}>
                      {a.state === 'error' ? a.error : a.state === 'uploading' ? 'Uploading…' : a.state === 'done' ? 'Uploaded' : fileSize(a.file.size)}
                    </div>
                  </div>
                  <Select aria-label={`${a.file.name} category`} value={a.category} onChange={(e) => setFile(a.key, { category: e.target.value as DocumentCategory })} disabled={a.state === 'uploading' || a.state === 'done'} className="w-[120px] shrink-0">
                    {BILL_DOCUMENT_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </Select>
                  {a.state !== 'uploading' && (
                    <button type="button" aria-label={`Remove ${a.file.name}`} onClick={() => setFiles((fs) => fs.filter((f) => f.key !== a.key))} className="shrink-0 rounded p-1 text-ink-300 hover:text-red-500"><X className="h-4 w-4" /></button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {files.some((f) => !fileOk(f.file)) && <p className="mt-2 text-[12px] text-amber-700">Remove the files marked in red before saving.</p>}
        </Card>
      )}
    </div>
  );
}
